const normalizeRole = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const normalizePermissionEntry = (permission) => {
  if (typeof permission === "string") {
    const value = permission.trim();
    return value || null;
  }

  if (permission && typeof permission.name === "string") {
    const value = permission.name.trim();
    return value || null;
  }

  return null;
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const toMatcher = (value) =>
  new RegExp(`^${escapeRegex(value).replace(/\\\*/g, ".*")}$`);

const getGrantedPermissions = (subject) => {
  const permissions = Array.isArray(subject?.permissions) ? subject.permissions : [];
  return permissions.map(normalizePermissionEntry).filter(Boolean);
};

const permissionMatches = (grantedPermission, requiredPermission) => {
  if (grantedPermission === "*" || requiredPermission === "*") {
    return true;
  }

  return (
    toMatcher(grantedPermission).test(requiredPermission) ||
    toMatcher(requiredPermission).test(grantedPermission)
  );
};

export const getRoleNames = (subject) => {
  const names = new Set();
  const roles = Array.isArray(subject?.roles) ? subject.roles : [];

  roles.forEach((role) => {
    if (typeof role === "string" && role.trim()) {
      names.add(role.trim());
      return;
    }

    if (role && typeof role.name === "string" && role.name.trim()) {
      names.add(role.name.trim());
    }
  });

  if (typeof subject?.role === "string" && subject.role.trim()) {
    names.add(subject.role.trim());
  }

  return Array.from(names);
};

export const hasRole = (subject, roleName) => {
  const expected = normalizeRole(roleName);
  if (!expected) return false;
  return getRoleNames(subject).some((role) => normalizeRole(role) === expected);
};

export const hasAnyRole = (subject, roleNames = []) => {
  if (!Array.isArray(roleNames)) return false;
  return roleNames.some((roleName) => hasRole(subject, roleName));
};

export const hasAllRoles = (subject, roleNames = []) => {
  if (!Array.isArray(roleNames)) return false;
  return roleNames.every((roleName) => hasRole(subject, roleName));
};

export const isAdminUser = (subject) =>
  hasAnyRole(subject, ["Admin", "Super Admin"]);

export const isSuperAdminUser = (subject) =>
  !!subject?.superAdmin || hasRole(subject, "Super Admin");

// A single role name to display for someone who may hold several. Admin tiers
// win; below them there is no ranking (Buyer / Ecom / Sales are peers, not a
// ladder) so the first assigned role is used. Mirrors backend RoleResolver.
export const getPrimaryRole = (subject) => {
  if (hasRole(subject, "Super Admin")) return "Super Admin";
  if (hasRole(subject, "Admin")) return "Admin";
  return getRoleNames(subject)[0] || "Sales";
};

export const hasPermission = (subject, permissionName) => {
  if (!subject) return false;
  if (isSuperAdminUser(subject)) return true;

  const requiredPermission = normalizePermissionEntry(permissionName);
  if (!requiredPermission) return false;

  return getGrantedPermissions(subject).some((grantedPermission) =>
    permissionMatches(grantedPermission, requiredPermission)
  );
};

export const hasAnyPermission = (subject, permissions = []) => {
  if (!Array.isArray(permissions)) return false;
  return permissions.some((permission) => hasPermission(subject, permission));
};

export const hasAllPermissions = (subject, permissions = []) => {
  if (!Array.isArray(permissions)) return false;
  return permissions.every((permission) => hasPermission(subject, permission));
};

export const hasFeatureGroup = (subject, featureGroupKey) => {
  if (!subject || !featureGroupKey) return false;
  if (isSuperAdminUser(subject)) return true;
  const fg = (subject.featureGroups || {})[featureGroupKey];
  return !!(fg && fg.hasAccess);
};
